import { LeftOutlined, LogoutOutlined, MenuOutlined, RightOutlined } from '@ant-design/icons'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AppearanceSwitch, ThemeIcon } from '../AppearanceSwitch'
import { useAuthStore } from '../../stores/authStore'
import { useResolvedTheme } from '../../stores/themeStore'
import { useSignOut } from './useSignOut'

/**
 * "More" at the foot of the side menu, after the website's web app
 * (Website/fursahub/components/WebApp/Shell/MoreMenu.tsx): a panel that opens
 * upwards with the signed-in account, Switch appearance (Light / Dark /
 * System) and Log out. The same on desktop (rail) and phones (drawer), so the
 * top bar no longer has to fit the appearance switch.
 */
export function MoreMenu() {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'main' | 'appearance'>('main')
  const rootRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const email = useAuthStore((s) => s.admin?.email)
  const dark = useResolvedTheme() === 'dark'
  const signOut = useSignOut()

  const close = () => {
    setOpen(false)
    setView('main')
  }

  // Closes on a click outside, Escape, or following a link — like the website.
  useEffect(close, [pathname])
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="fh-more" ref={rootRef} data-open={open || undefined}>
      {open && (
        <div className="fh-more-panel" role="menu" aria-label="More">
          {view === 'main' ? (
            <>
              {email && (
                <div className="fh-more-account">
                  <span className="fh-more-account-label">Signed in as</span>
                  <span className="fh-more-account-email">{email}</span>
                </div>
              )}
              <button type="button" className="fh-more-row" role="menuitem" onClick={() => setView('appearance')}>
                <ThemeIcon option={dark ? 'dark' : 'light'} size={20} />
                <span className="fh-more-label">Switch appearance</span>
                <RightOutlined className="fh-more-chevron" />
              </button>
              <div className="fh-more-divider" />
              <button type="button" className="fh-more-row" role="menuitem" onClick={signOut}>
                <LogoutOutlined className="fh-more-icon" />
                <span className="fh-more-label">Log out</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="fh-more-back" onClick={() => setView('main')}>
                <LeftOutlined />
                <span>Switch appearance</span>
              </button>
              <div className="fh-more-divider" />
              <AppearanceSwitch variant="list" />
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className="fh-nav-link"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="fh-nav-disc">
          <MenuOutlined />
        </span>
        <span className="fh-nav-label">More</span>
      </button>
    </div>
  )
}
